import { useQuery } from '@tanstack/react-query';

import studyServices from 'services/studies';

// The response runs to hundreds of KB, and screeners are created rarely, so the
// default `staleTime: 0` would refetch the whole list every time a consumer mounts.
const STALE_TIME_MS = 5 * 60 * 1000;

/**
 * @typedef {Object} AccountScreener
 * @property {number} screener_id
 * @property {string} screener_title
 * @property {number} study_id
 * @property {string} study_title
 */

/**
 * Query hook for every live screener on the account, across all studies.
 *
 * The response can run to hundreds of KB on large accounts, so mount the consumer
 * only when the list is genuinely needed (or pass `enabled`) rather than fetching
 * this alongside a page's other data.
 *
 * @param {Object} [params]
 * @param {boolean} [params.includeIntegrations=false] Include screeners backed by an
 *   external survey tool. They record completions, so anything filtering on
 *   completion needs them; pickers that edit a local form must not offer them.
 * @param {boolean} [params.excludeDeletedStudies=false] Drop screeners whose study was
 *   deleted.
 * @param {import('@tanstack/react-query').UseQueryOptions<AccountScreener[]>} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<AccountScreener[]>}
 */
export function useAllAccountScreenersQuery(
    { includeIntegrations = false, excludeDeletedStudies = false } = {},
    options = {}
) {
    return useQuery({
        // Both flags are part of the key: they change which screeners come back,
        // so two callers asking for different sets must not share a cache entry.
        queryKey: ['all-account-screeners', { includeIntegrations, excludeDeletedStudies }],
        queryFn: () => studyServices.getAllAccountScreeners({ includeIntegrations, excludeDeletedStudies }),
        staleTime: STALE_TIME_MS,
        ...options
    });
}
